<template>
  <v-data-table v-bind="dataTableOptions" :items="nodes" :item-key="itemKey" class="elevation-0 content">
    <template v-slot:top>
      <slot name="top" v-bind="{ nodes, headers }"></slot>
    </template>
    <template v-slot:item="{ item: node, headers }">
      <slot name="item" v-bind="{ node, headers }">
        <!-- eslint-disable-next-line prettier/prettier -->
        <tr
          v-show="showNode(node)" :class="{ 'v-data-table__selected': node._selected }"
          @click="onClickRow($event, node)" @click.right.prevent="onClickRowRight($event, node)"
        >
          <td v-for="(header, index) in headers" :key="index" :class="`text-${header.align ? header.align : 'start'}`">
            <span v-if="header.expandable">
              <span :style="nodeStyle(node)"></span>
              <expandable-btn :node="node" @click="toggleNode"></expandable-btn>
            </span>
            <slot :name="`item.${header.value}`" v-bind="{ item: node, headers, value: node[header.value] }">
              {{ node[header.value] }}
            </slot>
          </td>
        </tr>
      </slot>
    </template>
    <template v-slot:footer>
      <slot name="footer" v-bind="{ nodes, headers }"></slot>
    </template>
  </v-data-table>
</template>

<script>
/**
 * 속성 중 Underscore(_) prefix가 붙은 것은 내부적으로 새롭게 추가한 속성으로
 * 사용자가 props으로 넘겨준 데이터의 속성 이름과 중복을 피하고
 * 또 Tree의 속성인지 사용자 데이터 속성인지 구분하기 위한 것임.
 *
 * TreeDataTable에 적용되어야하는 VDataTable의 속성들(fixedHeader, dense, disablePagination)은
 * 기본적으로 props의 정의하여 VDataTable에 적용될 수 있도록 했다.
 * 더불어 click:row, contextmenu:row 의 이벤트도 대응될 수 있도록 구현되어있다.
 * 추후 더 많은 속성 및 이벤트가 필요할 경우 그때 그때 추가하도록한다.
 */
import { flattenDeep, merge, cloneDeep } from 'lodash';
import ExpandableBtn from './ExpandableBtn';

export default {
  name: 'TreeDataTable',
  components: {
    ExpandableBtn,
  },
  props: {
    // VDataTable options
    headers: Array,
    items: [Array, Object],
    itemKey: String,
    height: [Number, String],
    fixedHeader: Boolean,
    dense: Boolean,
    disablePagination: Boolean,
    hideDefaultFooter: Boolean,
    singleSelect: Boolean,
    loading: Boolean,
    itemsPerPage: [Number, String],

    // TreeDataTable options
    openAll: Boolean,
  },
  data() {
    return {
      nodes: [],
    };
  },
  computed: {
    dataTableOptions() {
      const options = merge({}, this.$props);
      // delete options.headers;
      delete options.openAll;
      return options;
    },
  },
  watch: {
    items: {
      immediate: true,
      handler(value) {
        if (Array.isArray(value)) {
          this.nodes = this.flattenNodes(cloneDeep(value));
        }
      },
    },
    openAll: {
      immediate: true,
      handler(value) {
        this.expandAll(this.nodes, value);
      },
    },
  },
  methods: {
    flattenNodes(nodes) {
      let flattenNodes = [];
      nodes.forEach(node => {
        node._depth = 1;
        // Commented.by.KWShin.2024.04.24.
        // _showed 속성을 가지고 구현하려고 했던 기능이 개발되다 말아서
        // 사실 _showed의 값은 트리의 행위에 영향을 미치지 않는다. 신경쓰지 마라.
        this.$set(node, '_showed', false);
        // Modified.by.KWShin.2024.04.20.
        // 부모 컴포넌트에서 전달되는 nodes 데이터를 통해 펼침 상태를 제어할 수 있도록
        // _expaned 속성이 존재할 때에는 이를 손대지 않는다.
        if (node._expanded == undefined) this.$set(node, '_expanded', false);
        flattenNodes.push(node);
        flattenNodes.push(this.flattenNode(node));
      });
      return flattenDeep(flattenNodes);
    },
    flattenNode(node, children = []) {
      if (node.children) {
        children = children.concat(node.children);
        node.children.forEach(child => {
          child._parentNode = node;
          child._depth = node._depth + 1;
          this.$set(child, '_showed', false);
          // Modified.by.KWShin.2024.04.20.
          // 부모 컴포넌트에서 전달되는 nodes 데이터를 통해 펼침 상태를 제어할 수 있도록
          // _expaned 속성이 존재할 때에는 이를 손대지 않는다.
          if (child._expanded == undefined) this.$set(child, '_expanded', false);
          children.splice(children.indexOf(child) + 1, 0, this.flattenNode(child));
        });
      }
      return flattenDeep(children);
    },
    /**
     * Modified.by.KWShin.2024.04.20.
     * ExpandableBtn이 클릭될 때는 _expanded 조작을 통해 자식 노드를 보이거나 감출 수 있다.
     * 하지만, 노드가 최상위 노드이거나, ExpandableBtn 조작 이외의 방법을 통해서는
     * 노드를 보이거나 감출 수 있는 방법이 없다.
     * 따라서, _deleted 속성을 추가하고, showNode()에서 보여줄 지 말지를 판단하는 기능을 추가했다.
     * 노드를 화면에 보이게 하거나 감추려면 _deleted에 true/false를 할당하고
     * TreeDataTable의 Props 'items'를 다시 할당해주면 된다.
     * 또한, 부모가 열려있더라도 부모의 조상중에 하나라도 닫혀있으면 보이지 않게 하는 기능도 추가했다.
     */
    /*
    showNode(node) {
      if (!node._parentNode) {
        return true;
      } else if (node._parentNode._expanded) {
        return true;
      } else {
        return false;
      }
    },
    */
    showNode(node) {
      if (node._deleted) return false;
      if (!node._parentNode) {
        return true;
      } else if (node._parentNode._expanded) {
        if (node._parentNode._depth == 1) {
          return true;
        } else {
          return this.showNode(node._parentNode);
        }
      } else {
        return false;
      }
    },
    nodeStyle(node) {
      return {
        paddingLeft: `${node._depth > 1 ? node._depth * 15 : 0}px`,
      };
    },
    /*
    expandAll(expanded) {
      this.nodes.forEach(node => {
        node._showed = expanded;
        node._expanded = expanded;
        if (expanded && !node._opened) {
          node._opened = true;
        }
      });
    },
    */
    expandAll(nodes, expanded) {
      nodes.forEach(node => {
        node._showed = expanded;
        node._expanded = expanded;
        if (expanded && !node._opened) {
          node._opened = true;
        }
        if (node.children) {
          this.expandAll(node.children, expanded);
        }
      });
    },
    /**
     * Modified.by.KWShin.2026.09.22.
     * 예전에는 노드를 펼칠 때 expandChildren()으로 자손 전체의 _expanded를 true로,
     * 접을 때 collapseChildren()으로 자손 전체의 _expanded를 false로 바꾸었다.
     * 그런데 _expanded는 ExpandableBtn이 아이콘 모양(chevron-down/chevron-right)을
     * 결정할 때 쓰는 값이기도 해서, 아직 자식을 읽어오지도 않은 자손 노드가
     * 이미 펼쳐진 것처럼 chevron-down으로 표시되는 문제가 있었다.
     * 그 아이콘을 클릭하면 펼쳐지는 대신 접히면서(_expanded=false) 자식만 몰래 읽어오고,
     * 한 번 더 클릭해야 비로소 펼쳐져 보이는 오동작으로 이어졌다.
     * 노드를 보여줄지 말지는 showNode()가 조상 체인을 모두 거슬러 올라가며 판단하므로
     * 접고 펼칠 때 자손의 _expanded를 건드릴 필요가 전혀 없다.
     * 건드리지 않으면 접기 전의 펼침 상태가 자손에 그대로 남아 있어
     * 다시 펼쳤을 때 접기 직전의 모양 그대로 복원된다.
     */
    toggleNode(node, { loading }) {
      node._showed = !node._expanded;
      node._expanded = !node._expanded;

      const vm = this;
      // Added.by.KWShin.2026.09.22.
      // 컴포넌트를 사용하는 곳이 자식을 읽어오는 동안 loading(true)/loading(false)를 호출하므로
      // 이를 가로채어 노드에 '자식을 읽어오는 중'임을 표시해둔다. hasChildren()에서 사용한다.
      const setLoading = value => {
        vm.$set(node, '_loading', value);
        loading(value);
      };
      this.$emit('click:expand', node, {
        expanded: node._expanded,
        loading: setLoading,
        add(nodes) {
          const addNode = (newNode, parentNode) => {
            vm.$set(newNode, '_showed', false);
            vm.$set(newNode, '_expanded', false);
            newNode._depth = parentNode._depth + 1;
            newNode._parentNode = parentNode;
            /**
             * Modified.by.KWShin.2026.09.22.
             * 서버가 여러 단계의 노드를 한꺼번에 내려주면 newNode.children에 자식이 이미 들어있고,
             * 그 자식들을 아래에서 다시 addNode()로 등록하는데
             * 그때 children에 같은 자식이 한 번 더 붙어 children 배열에 중복이 생기고 있었다.
             * 중복이 생기면 이 노드를 펼칠 때 컴포넌트를 사용하는 곳에서 같은 자식을 두 번 처리하게 되고,
             * 두 번째 처리에서 이미 지워진 자식을 remove()/update()로 다시 건드리면서
             * 아무 상관 없는 노드가 트리에서 사라졌다.
             * 그래서 이미 등록되어 있는 자식은 다시 붙이지 않는다.
             */
            if (!parentNode.children) vm.$set(parentNode, 'children', []);
            if (!parentNode.children.includes(newNode)) {
              parentNode.children = parentNode.children.concat(newNode);
            }
            // parentNode._showed = true;

            const index = vm.nodes.indexOf(parentNode);
            ///////////////////////////////////////////////////////////////////////////////////////////////
            // 다음과 같이 splice를 하면 추가되는 자식이 여러개일 때, 매달리는 자식의 순서가 뒤집히게 된다.
            // Modified.by.KWShin.2026.09.22.
            // 부모가 이미 트리에서 제거된 뒤라면 index가 -1이고, 그러면 splice(0, 0, ...)이 되어
            // 자식이 엉뚱하게 트리의 맨 위에 붙는다. 부모가 살아있을 때에만 끼워 넣는다.
            if (index > -1) vm.nodes.splice(index + 1, 0, newNode);
            ///////////////////////////////////////////////////////////////////////////////////////////////
            // 그래서 이렇게 바꿈.2020.12.28.KWShin.
            // const childLength = parentNode.children.length;
            // vm.nodes.splice(index + childLength, 0, newNode);
            // 그런데, 바꾸었더니 User history/Program history에서 다양한 현상이 발생한다.
            // - 자식이 추가되는 위치의 문제, 자식이 원래 있는 경우와 expand시 읽어 온 경우의 문제 등...
            // 일단 원복하고 expand시 추가되는 children 배열은 reverse()하여 추가함.
            // 뭔가 수정이 필요할 듯... 2021.01.11.KWShin.
            ///////////////////////////////////////////////////////////////////////////////////////////////
            if (newNode.children && newNode.children.length > 0) {
              for (const child of newNode.children) {
                addNode(child, newNode);
              }
            }
          };

          const newNodes = Array.isArray(nodes) ? nodes : [nodes];
          for (const newNode of newNodes) {
            addNode(newNode, node);
          }

          // for (const newNode of newNodes) {
          //   vm.$set(newNode, '_showed', false);
          //   vm.$set(newNode, '_expanded', false);
          //   newNode._depth = node._depth + 1;
          //   newNode._parentNode = node;

          //   // 신규 노드(newNode)를 클릭한 노드(node)의 자식으로 등록
          //   node.children = node.children ? node.children.concat(newNode) : [newNode];

          //   // 자식으로 등록된 노드가 있으므로 node._showed를 true로 설정
          //   // TODO. 상위 노드에서 트리를 접었다 펼칠 때 접기전 형태 그대로 펼치기 위해
          //   //       _expanded 속성 외 _showed라는 추가적인 상태 값을 사용하는데
          //   //       두 개의 상태를 기준으로 하나의 행위(접고 펼치는)를 정의하려니..
          //   //       복잡하고 사실 상태 추적도 잘 안됨... ㅠㅠ 시간 날 때 천천히 고쳐야 할 듯..
          //   //       근데 정말 리펙토링할 수 있을까?? ㅋ
          //   node._showed = true;
          //   const index = vm.nodes.indexOf(node);
          //   vm.nodes.splice(index + 1, 0, newNode);
          // }
        },
        /**
         * Modified.by.KWShin.2026.09.22.
         * 트리에 없는 노드를 넘겨받으면 indexOf()가 -1을 리턴하는데,
         * splice(-1, 1)은 맨 마지막 노드를 지우고 splice(-1, 1, node)는 맨 마지막 노드를 덮어쓴다.
         * 그래서 이미 지워진 노드를 한 번 더 지우려 할 때
         * 펼치지도 않은 엉뚱한 노드가 트리에서 사라지는 일이 있었다.
         */
        remove(node) {
          const index = vm.nodes.indexOf(node);
          if (index > -1) vm.nodes.splice(index, 1);
        },
        update(node) {
          const index = vm.nodes.indexOf(node);
          if (index > -1) vm.nodes.splice(index, 1, node);
        },
        /**
         * Modified.by.KWShin.2026.09.22.
         * 지금 펼치고 있는 노드(node)가 아닌 다른 노드가 아직 자식을 읽어오는 중이라면
         * 자식이 없다고 판단해서는 안 된다.
         * 부모가 자식을 하나씩 붙여가는 도중에 먼저 붙은 자식을 펼쳐서 그 자식이 제거되면,
         * 아직 붙지 않은 나머지 자식들 때문에 부모까지 빈 노드로 판정되어 지워지는 일이 있었다.
         */
        hasChildren(target) {
          if (target !== node && target._loading) return true;
          for (const child of target.children) {
            const index = vm.nodes.indexOf(child);
            if (index > -1) {
              return true;
            }
          }
          return false;
        },
      });

      // 컴포넌트를 사용하는 곳에서 트리를 오픈할 때 이미 한 번 오픈한 Node인지 식별하기 위한 설정 값
      // 보통 비동기 통신에서 한 번만 데이터를 가져오기 위해 사용됨
      if (!node._opened && node._expanded) {
        node._opened = true;
      }
    },
    onClickRow(e, node) {
      const tagName = e.target.tagName;
      if (tagName === 'I' || tagName === 'BUTTON') {
        return;
      }
      this.emit('click:row', node, node);
    },
    onClickRowRight(e, node) {
      this.emit('contextmenu:row', node, e);
    },
    emit(name, node, arg) {
      const vm = this;
      this.$emit(name, arg, {
        headers: this.headers,
        expanded: node._expanded,
        selected: node._selected,
        node: node,
        select(value) {
          vm.$set(node, '_selected', value);
          if (value && vm.singleSelect) {
            const selectedIndex = vm.nodes.indexOf(node);
            vm.nodes.forEach((n, index) => {
              if (index !== selectedIndex) {
                if (n._selected) {
                  n._selected = false;
                  return false;
                }
              }
            });
          }
        },
      });
    },
  },
};
</script>

<style lang="scss" scoped></style>
